import {
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Grow,
} from '@mui/material'
import React from 'react'
import {
  UseFormGetValues,
  UseFormSetValue,
  useWatch,
  Control,
} from 'react-hook-form'
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  Autocomplete,
} from '@react-google-maps/api'

import { FormValues } from '..'
import { useTranslation } from 'react-i18next'
import { PriorityHighRounded } from '@mui/icons-material'

interface MapProps extends google.maps.MapOptions {
  control: Control<FormValues>
  getValues: UseFormGetValues<FormValues>
  setValue: UseFormSetValue<FormValues>
}

function MapComponent({ getValues, setValue, control }: MapProps) {
  const { t } = useTranslation()

  const watchX = useWatch({ control, name: 'location.x' })
  const watchY = useWatch({ control, name: 'location.y' })

  const CLEAR_BUTTON_DISABLED = !watchX && !watchY

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_MAPS_KEY,
    libraries: ['places'],
    region: 'GR',
    language: 'el',
  })

  const [map, setMap] = React.useState<google.maps.Map | null>(null)
  const [center] = React.useState<google.maps.LatLngLiteral>({
    lat: getValues('location.x') ?? 37.9838,
    lng: getValues('location.y') ?? 23.7275,
  })

  React.useEffect(() => {
    if (map) {
      map.setZoom(14)
    }
  }, [map])

  const [selected, setSelected] = React.useState<
    | {
        lat: number
        lng: number
      }
    | undefined
  >(() => {
    const x = getValues('location.x')
    const y = getValues('location.y')
    if (!!x && !!y) {
      return {
        lat: x,
        lng: y,
      }
    } else return
  })

  const onLoad = React.useCallback(function callback(map) {
    const bounds = new window.google.maps.LatLngBounds(center)
    map.fitBounds(bounds)
    map.setZoom(14)
    setMap(map)
  }, [])

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null)
  }, [])

  const [mapAutocomplete, setMapAutocomplete] =
    React.useState<google.maps.places.Autocomplete | null>(null)

  const onLoadAutocomplete = (autoComplete) => {
    setMapAutocomplete(autoComplete)
  }

  const onPlaceChanged = async () => {
    if (mapAutocomplete !== null) {
      const place = mapAutocomplete.getPlace()
      try {
        if (place?.geometry?.location && map) {
          const latitude = place.geometry.location.lat()
          const longitude = place.geometry.location.lng()
          setSelected({ lat: latitude, lng: longitude })
          setValue('location.x', latitude)
          setValue('location.y', longitude)
          map.panTo({ lat: latitude, lng: longitude })
          map.setZoom(14)
          if (place?.address_components?.length) {
            const foundStrNumber = place?.address_components.find((component) =>
              component?.types?.includes('street_number')
            )
            if (foundStrNumber) {
              setValue(
                'location.addresses.0.streetNumber',
                foundStrNumber?.long_name
              )
            } else setValue('location.addresses.0.streetNumber', '')

            const foundStreet = place?.address_components.find((component) =>
              component?.types?.includes('route')
            )
            if (foundStreet) {
              setValue('location.addresses.0.street', foundStreet?.long_name)
            } else setValue('location.addresses.0.street', '')

            const foundCity = place?.address_components.find(
              (component) =>
                component?.types?.includes('locality') &&
                component?.types?.includes('political')
            )
            if (foundCity) {
              setValue('location.addresses.0.city', foundCity?.long_name)
            } else setValue('location.addresses.0.city', '')

            // country handling to be added later,value initialized in form
            // const foundCountry = place?.address_components.find((component) =>
            //   component?.types?.includes('country')
            // )
            // if (foundCountry) {
            //   setValue('location.addresses.0.country', foundCountry?.long_name)
            // } else setValue('location.addresses.0.country', '')

            const foundPostalCode = place?.address_components.find(
              (component) => component?.types?.includes('postal_code')
            )
            if (foundPostalCode) {
              setValue(
                'location.addresses.0.postalCode',
                foundPostalCode?.long_name?.replace(' ', '')
              )
            } else setValue('location.addresses.0.postalCode', '')
          }
        }
      } catch (err) {
        console.error(err)
      }
    }
  }

  const onMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    if (e?.latLng && map) {
      const latitude = e.latLng.lat()
      const longitude = e.latLng.lng()
      map.panTo({ lat: latitude, lng: longitude })
      map.setZoom(14)
      setValue('location.x', latitude)
      setValue('location.y', longitude)
      setValue('location.addresses.0.streetNumber', '')
      setValue('location.addresses.0.street', '')
      setValue('location.addresses.0.city', '')
      setValue('location.addresses.0.postalCode', '')
      setValue('location.addresses.0.area', '')
    }
  }

  const handleClear = () => {
    setSelected(undefined)
    setValue('location.x', null)
    setValue('location.y', null)
    setValue('location.addresses.0.streetNumber', '')
    setValue('location.addresses.0.street', '')
    setValue('location.addresses.0.city', '')
    setValue('location.addresses.0.postalCode', '')
    setValue('location.addresses.0.area', '')
  }

  if (loadError) {
    return <div>{t('manageEstateAd.steps.estateLocation.mapError')}</div>
  }
  if (isLoaded) {
    return (
      <>
        <Grid
          container
          direction="row"
          alignItems="stretch"
          wrap="nowrap"
          spacing={2}
        >
          <Grid item xs>
            <Autocomplete
              types={['geocode']}
              onLoad={onLoadAutocomplete}
              onPlaceChanged={onPlaceChanged}
              fields={[
                'geometry.location',
                'formatted_address',
                'adr_address',
                'address_component',
                'name',
                'place_id',
                'type',
              ]}
            >
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t(
                  'manageEstateAd.steps.estateLocation.searchOnMap'
                )}
              />
            </Autocomplete>
          </Grid>
          <Grid
            item
            sx={{
              height: '100%',
            }}
          >
            <Button
              variant="contained"
              disabled={CLEAR_BUTTON_DISABLED}
              onClick={handleClear}
              sx={{
                textTransform: 'none',
                boxShadow: 'none',
                height: '100%',
                minWidth: 100,
              }}
            >
              {t('manageEstateAd.steps.estateLocation.removeCoords')}
            </Button>
          </Grid>
        </Grid>

        <GoogleMap
          clickableIcons={false}
          mapContainerStyle={{
            width: '100%',
            height: '400px',
          }}
          center={center}
          zoom={14}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            keyboardShortcuts: false,
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: false,
            rotateControl: false,
          }}
        >
          {selected && (
            <Marker position={selected} onDragEnd={onMarkerDragEnd} draggable />
          )}
          <Grow in={Boolean(selected)}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: `white`,
                border: (theme) => `2px solid ${theme.palette.primary.main}`,
                boxShadow: 1,
                px: 0.6,
                py: 0.3,
                position: 'absolute',
                left: '10px',
                top: '10px',
                borderRadius: 2.5,
              }}
            >
              <PriorityHighRounded
                color="primary"
                sx={{
                  fontSize: '1.5rem',
                  marginRight: 1,
                }}
              />
              <Typography variant="caption" sx={{ marginRight: 1 }}>
                {t('manageEstateAd.steps.estateLocation.dragMarker')}
              </Typography>
            </Box>
          </Grow>
        </GoogleMap>
      </>
    )
  }
  return <CircularProgress />
}

export default React.memo(MapComponent)
